import { useEffect, useRef, useState } from 'react'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { agenciesService, type AdminAgencyRecord } from '@/services/agencies'

/**
 * Agency picker backed by the paginated /admin/agencies search.
 *
 * Search runs server-side, so cmdk's own filtering is switched off: leaving it
 * on would filter the current page a second time and hide results the server
 * had already matched.
 */
export function AgencyCombobox({
  value,
  onChange,
  id,
  placeholder = 'Select an agency',
}: {
  /** publicId of the selected agency, or '' for none. */
  value: string
  onChange: (publicId: string) => void
  id?: string
  placeholder?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [agencies, setAgencies] = useState<AdminAgencyRecord[]>([])
  const [isLoading, setIsLoading] = useState(false)
  // The selected agency can drop out of the current result page, so its name is
  // kept here rather than looked up in `agencies` on every render.
  const [selectedName, setSelectedName] = useState('')
  const requestRef = useRef(0)

  useEffect(() => {
    if (!open) return
    const requestId = ++requestRef.current
    setIsLoading(true)
    const timer = setTimeout(async () => {
      try {
        const res = await agenciesService.getAgencies(1, 20, search)
        // A slower earlier request must not overwrite a newer one's results.
        if (requestId === requestRef.current) setAgencies(res.data)
      } catch {
        if (requestId === requestRef.current) setAgencies([])
      } finally {
        if (requestId === requestRef.current) setIsLoading(false)
      }
    }, 250)
    return () => clearTimeout(timer)
  }, [search, open])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !value && 'text-muted-foreground')}>
            {value ? selectedName || value : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search agencies" value={search} onValueChange={setSearch} />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching
              </div>
            ) : (
              <>
                <CommandEmpty>No agencies match that search</CommandEmpty>
                <CommandGroup>
                  {agencies.map((agency) => (
                    <CommandItem
                      key={agency.id}
                      value={agency.id}
                      onSelect={() => {
                        onChange(agency.id)
                        setSelectedName(agency.name)
                        setOpen(false)
                      }}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate">{agency.name}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {agency.superAdminEmail || agency.id}
                        </div>
                      </div>
                      <Check
                        className={cn(
                          'ml-2 h-4 w-4 shrink-0',
                          value === agency.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
